import React, { useState, useEffect, useRef } from 'react';
import { Container, TextField, Button, Box, Typography, FormControl, Tooltip, MenuItem, Select } from '@mui/material';
import { createItemType, getKB, updateItemType } from "../API/KB/KBAPI.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {  colorText, colorTextInactive, mobileButton } from "../App/theme.js";
import LocalLoader from "../Loaders/LocalLoader.js";
import { deepClone, isMobile } from "../App/utils.js";
import { BackButton } from "../KB/BackButton.js";
import { EmbeddingModels } from "../constants";
import { FieldWithActionButtons } from "./ItemHelpers";
import { PaperWrapper } from "../Components/PaperWrapper";
import { searchProps, searchTypes } from "./contants";
import { EditAttribute } from "./EditAttribute";
import { CreateAttribute } from "./CreateAttribute";
import {backButtonMt, buttonVerticalMargin, subtitleMarginBottom, subtitleMarginTop, titleMt} from "../App/style";

function propToType(str) {
    return str.replace(/\d+$/, '');
}

const ItemType = ({ injectedItemType }) => {
    const [isLoading, setLoading] = useState(false);
    const [itemTypeToCreate, setItemTypeToCreate] = useState('');
    const [KBData, setKBData] = useState(null);
    const buttonSubmitItemRef = useRef();
    const [embeddingTemplate, setEmbeddingTemplate] = useState('');
    const [attributes, setAttributes] = useState([]);
    const [selectedType, setSelectedType] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newAttribute, setNewAttribute] = useState({ attrName: '', label: '', placeholder: '' });
    const [editAttribute, setEditAttribute] = useState(null);
    const [itemTypeToEdit, setItemTypeToEdit] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [lastFetch, setLastFetch] = useState(Date.now());

    useEffect(() => {
        if (itemTypeToEdit) return;

        let template = ``;
        if ( attributes ) {
            attributes.forEach( o => {
                if (o.semanticSearch) {
                    template += `\${item.${o.attrName}} `;
                }
            })
            setEmbeddingTemplate(template)
        }

        if (Date.now() - lastFetch >= 100) setHasChanges(true);

    }, [attributes])

    const getAvailableProp = (type) => {
        if (availableSearchProps[`${type}`]) return type

        for (let i = 0; i < 20; i++) {
            if (availableSearchProps[`${type}${i}`]) {
                return `${type}${i}`
            }
        }
    }

    let itemType = useParams().itemType;
    if (injectedItemType) itemType = injectedItemType;

    const navigate = useNavigate();
    const location = useLocation();

    const availableSearchProps = Object.keys(searchProps)
        .filter(prop => !attributes.some(attr => attr.attrType === prop))
        .reduce((acc, key) => (acc[key] = searchProps[key], acc), {});

    useEffect(() => {
        getKB().then(data => {
            const { itemTypes } = data;
            setKBData(data)

            if (itemTypes) {
                if (itemTypes?.[itemType]) {
                    setItemTypeToEdit(itemType)
                    setAttributes(itemTypes[itemType].attributes)
                    setEmbeddingTemplate(itemTypes[itemType].embeddingTemplate)
                    setLastFetch(Date.now());
                }
            }
        })

    }, [location])

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.ctrlKey || event.metaKey) {
                switch (String.fromCharCode(event.which).toLowerCase()) {
                    case 's':
                        event.preventDefault();
                        if (buttonSubmitItemRef.current) buttonSubmitItemRef.current.click();
                        break;
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const myAttributes = attributes.map(({semanticSearch, ...restAttribute}) => restAttribute)

            if (itemType) {
                await updateItemType(itemType, myAttributes, embeddingTemplate);
                setHasChanges(false)
                setLoading(false);
            } else {
                const { itemType } = await createItemType(itemTypeToCreate, myAttributes, embeddingTemplate);
                setHasChanges(false)
                setLoading(false);
                navigate(`/edit-item-type/` + itemType);
            }

        } catch (error) {
            alert(error);
            setLoading(false);
            console.error('Error saving item:', error);
        }
    };

    const handleDialogClose = (isCancel) => {
        setDialogOpen(false);
        setNewAttribute({ attrName: '', label: '', placeholder: '' });
    };

    const handleDialogSave = () => {
        setAttributes(prev => [...prev, newAttribute]);
        handleDialogClose();
    };

    const handleEditCreate = () => {
        setAttributes(prev => prev.map(attr => attr.attrType === editAttribute.attrType ? editAttribute : attr));
        setEditAttribute(null);
    };

    const handleDragStart = (index) => (event) => {
        event.dataTransfer.setData('text/plain', index);
    };

    const handleDrop = (index) => (event) => {
        event.preventDefault();
        const draggedIndex = event.dataTransfer.getData('text/plain');
        if (draggedIndex !== index) {
            const updatedAttributes = [...attributes];
            const [draggedItem] = updatedAttributes.splice(draggedIndex, 1);
            updatedAttributes.splice(index, 0, draggedItem);
            setAttributes(updatedAttributes);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    if (!KBData) return null;

    const withEmbeddings = EmbeddingModels?.[KBData?.embeddingModel];
    const mt = 4;
    const readyToCreate = !itemType && itemTypeToCreate && attributes?.length;
    const readyToSave = itemType && itemTypeToEdit && attributes?.length;
    const formIsReady = readyToCreate || readyToSave;

    return (
        <Container>
            <LocalLoader open={isLoading} blocking={true} />

            <Box mt={backButtonMt} display="flex" justifyContent="space-between">
                <BackButton backToItems={true} />
            </Box>

            <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}>
                <Box mt={titleMt}
                     display="flex"
                     justifyContent={isMobile ? "center" : "flex-start"}
                >
                    <Typography variant="h4" style={{ color: colorText }}>{itemType
                        ? `Edit ${itemTypeToEdit} Type`
                        : `Create ${itemTypeToCreate ? itemTypeToCreate : 'Item'} Type`
                    }</Typography>
                </Box>


                <Box display="flex" justifyContent={isMobile ? "center" : "flex-start"} style={{
                    marginBottom: subtitleMarginBottom,
                    marginTop: subtitleMarginTop
                }}>
                    <Typography variant="subtitle1" style={{ color: colorTextInactive }}>
                        {itemType ? 'Edit' : 'Create'} an item template, and add or remove typed attributes for advanced structured and semantic search.
                    </Typography>
                </Box>

                <PaperWrapper>
                    <Box mt={1} >
                        <Tooltip
                            placement={"top-start"}
                            title={"Specify the name of the entity for this item type."}>
                            <TextField
                                autoComplete="off"
                                label="Item Type"
                                value={itemTypeToEdit || itemTypeToCreate || ''}
                                onChange={(e) => setItemTypeToCreate(e.target.value)}
                                fullWidth
                                disabled={!!itemTypeToEdit}
                                variant="outlined"
                                placeholder="Type entity name (e.g., Product, Article, etc.)"
                            />
                        </Tooltip>
                    </Box>

                    <Typography display="flex" justifyContent={isMobile ? "center" : "flex-start"} variant="h5"
                                style={{ color: colorText, paddingTop: 32, paddingBottom: 0 }}>
                        Attributes
                    </Typography>

                    {/**
                     Attributes Section
                     */}

                    {attributes.map((attr, index) => {
                        return (
                            <div
                                key={index}
                                draggable
                                onDragStart={handleDragStart(index)}
                                onDrop={handleDrop(index)}
                                onDragOver={handleDragOver}
                            >
                                <FieldWithActionButtons
                                    {...{ attr, setEditAttribute, setAttributes, mt, index }} />
                            </div>
                        );
                    })}

                    <Box mt={1} sx={{ marginTop: '32px', marginBottom: '32px' }} display="flex" justifyContent={isMobile ? "center" : "flex-start"}>
                        <FormControl variant="outlined">
                            <Select
                                value={selectedType}
                                onChange={(e) => {
                                    const attrType = getAvailableProp(e.target.value);
                                    const type = propToType(attrType);
                                    const itemIdField = attrType === 'itemId' ? 'id' : '';

                                    const attrToAdd = {
                                        attrName: itemIdField || '',
                                        attrType,
                                        label: itemIdField || '',
                                        placeholder: itemIdField || ''
                                    };

                                    setNewAttribute(attrToAdd);
                                    setDialogOpen(attrType);
                                }}
                                displayEmpty
                                renderValue={() => <>
                                    +&nbsp;&nbsp;<>New Attribute</>
                                </>}
                                style={{ minWidth: 200, maxHeight: 36 }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 500, // Set the maximum height for the dropdown
                                        },
                                    },
                                }}
                            >
                                {searchTypes.reduce((menuItems, o) => {
                                    if (Object.keys(availableSearchProps).some(prop => prop.startsWith(o.type) && (prop?.length - o.type?.length) < 3)) {
                                        const categoryExists = menuItems.some(item => item.category === o.category);
                                        if (!categoryExists && o.category) {
                                            const categoryHasItems = searchTypes.some(
                                                item => item.category === o.category &&
                                                    Object.keys(availableSearchProps).some(prop => prop.startsWith(item.type) && (prop?.length - item.type?.length) < 3)
                                            );
                                            if (categoryHasItems) {
                                                menuItems.push({
                                                    category: o.category,
                                                    isCategoryHeader: true,
                                                });
                                            }
                                        }
                                        menuItems.push(
                                            <MenuItem key={o.type} value={o.type}>
                                                {o.category ? <span style={{ marginLeft: '16px' }}>{o.title || o.type}</span> : <span style={{ width: '100px' }}>{o.title || o.type}</span>} {o.description}
                                            </MenuItem>
                                        );
                                    }
                                    return menuItems;
                                }, []).map((item, index) => (
                                    item.isCategoryHeader ? (
                                        <MenuItem autoFocus={true} key={`category-${index}`} disabled>
                                            <span style={{ fontWeight: 'bold', marginLeft: '-8px' }}>{item.category}</span>
                                        </MenuItem>
                                    ) : (
                                        item
                                    )
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {withEmbeddings && attributes?.length > 0 &&
                        <Box mt={mt} style={{ paddingBottom: 8 }} >
                            <Tooltip
                                title="Specify a template for item attributes to be stored as embeddings for semantic searchability. Example: ${item.itemId} ${item.body}"
                                placement="bottom-end">
                                <TextField
                                    autoComplete="off"
                                    label="Embedding Template"
                                    value={embeddingTemplate || ''}
                                    onChange={(e) => setEmbeddingTemplate(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="${item.field1} ${item.field2} ${item.field3}"
                                />
                            </Tooltip>
                        </Box>
                    }
                </PaperWrapper>

                <Box mt={0} display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
                    <Button type="submit"
                            ref={buttonSubmitItemRef}
                            variant="contained"
                            color="secondary"
                            style={{ ...mobileButton, marginTop: buttonVerticalMargin }}
                            disabled={!formIsReady}
                    >
                        {itemType ? 'Save' : 'Create'}
                    </Button>
                </Box>
            </form>

            <CreateAttribute {...{ newAttribute, setNewAttribute, handleDialogClose, dialogOpen, handleDialogSave, itemTypeToCreate, existingAttributes: attributes }} />
            <EditAttribute {...{ editAttribute, setEditAttribute, handleEditCreate, itemTypeToCreate }} />
        </Container>
    );
};

export default ItemType;